/**
 * 主题管理工具
 * 管理绿色环保主题、深色模式和自定义主题
 */

// 主题类型定义
export type ThemeMode = 'light' | 'dark' | 'auto'
export type ThemeVariant = 'default' | 'eco' | 'minimal' | 'vibrant'

// 主题配置接口
export interface ThemeConfig {
  mode: ThemeMode
  variant: ThemeVariant
  primaryColor: string
  accentColor: string
  fontSize: number
  borderRadius: number
  animations: boolean
  highContrast: boolean
}

// 默认主题配置
export const DEFAULT_THEME_CONFIG: ThemeConfig = {
  mode: 'auto',
  variant: 'eco',
  primaryColor: '#004542',
  accentColor: '#4CAF50',
  fontSize: 16,
  borderRadius: 4,
  animations: true,
  highContrast: false
}

// 主题变体配置
export const THEME_VARIANTS: Record<ThemeVariant, {
  name: string
  description: string
  colors: {
    primary: string
    accent: string
    success: string
    warning: string
    error: string
    info: string
  }
}> = {
  default: {
    name: 'Default',
    description: 'Standard green theme',
    colors: {
      primary: '#004542',
      accent: '#4CAF50',
      success: '#4CAF50',
      warning: '#FF9800',
      error: '#F44336',
      info: '#2196F3'
    }
  },
  eco: {
    name: 'Eco Green',
    description: 'Enhanced environmental theme',
    colors: {
      primary: '#2E7D32',
      accent: '#66BB6A',
      success: '#4CAF50',
      warning: '#FFA726',
      error: '#EF5350',
      info: '#42A5F5'
    }
  },
  minimal: {
    name: 'Minimal',
    description: 'Clean and minimal design',
    colors: {
      primary: '#388E3C',
      accent: '#81C784',
      success: '#4CAF50',
      warning: '#FFB74D',
      error: '#E57373',
      info: '#64B5F6'
    }
  },
  vibrant: {
    name: 'Vibrant',
    description: 'Bold and energetic colors',
    colors: {
      primary: '#1B5E20',
      accent: '#4CAF50',
      success: '#4CAF50',
      warning: '#FF8F00',
      error: '#D32F2F',
      info: '#1976D2'
    }
  }
}

// 本地存储键名
const STORAGE_KEY = 'carbon-trading-theme'

// 当前主题配置
let currentTheme: ThemeConfig = { ...DEFAULT_THEME_CONFIG }

/**
 * 获取系统主题偏好
 */
export function getSystemThemePreference(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light'
  
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * 检测是否偏好减少动画
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * 检测是否偏好高对比度
 */
export function prefersHighContrast(): boolean {
  if (typeof window === 'undefined') return false
  
  return window.matchMedia('(prefers-contrast: high)').matches
}

/**
 * 从本地存储加载主题配置
 */
export function loadThemeConfig(): ThemeConfig {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      currentTheme = { ...DEFAULT_THEME_CONFIG, ...parsed }
    }
  } catch (error) {
    console.warn('Failed to load theme config:', error)
    currentTheme = { ...DEFAULT_THEME_CONFIG }
  }

  // 应用系统偏好
  if (currentTheme.mode === 'auto') {
    currentTheme.mode = getSystemThemePreference()
  }

  if (prefersReducedMotion()) {
    currentTheme.animations = false
  }

  if (prefersHighContrast()) {
    currentTheme.highContrast = true
  }

  return currentTheme
}

/**
 * 保存主题配置到本地存储
 */
export function saveThemeConfig(config: ThemeConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config))
    currentTheme = { ...config }
  } catch (error) {
    console.warn('Failed to save theme config:', error)
  }
}

/**
 * 获取当前主题配置
 */
export function getCurrentThemeConfig(): ThemeConfig {
  return { ...currentTheme }
}

/**
 * 应用主题到DOM
 */
export function applyTheme(config: ThemeConfig): void {
  const root = document.documentElement
  const variant = THEME_VARIANTS[config.variant]

  // 设置CSS自定义属性
  root.style.setProperty('--theme-primary', config.primaryColor)
  root.style.setProperty('--theme-accent', config.accentColor)
  root.style.setProperty('--theme-success', variant.colors.success)
  root.style.setProperty('--theme-warning', variant.colors.warning)
  root.style.setProperty('--theme-error', variant.colors.error)
  root.style.setProperty('--theme-info', variant.colors.info)
  root.style.setProperty('--theme-font-size', `${config.fontSize}px`)
  root.style.setProperty('--theme-border-radius', `${config.borderRadius}px`)

  // 设置主题模式类
  root.classList.remove('theme-light', 'theme-dark')
  // const effectiveMode = config.mode === 'auto' ? getSystemThemePreference() : config.mode
  // root.classList.add(`theme-${effectiveMode}`)
  // 强制使用浅色主题
  root.classList.add(`theme-light`)
  

  // 设置主题变体类
  root.classList.remove('theme-default', 'theme-eco', 'theme-minimal', 'theme-vibrant')
  root.classList.add(`theme-${config.variant}`)

  // 设置动画偏好
  if (!config.animations || prefersReducedMotion()) {
    root.classList.add('theme-no-animations')
  } else {
    root.classList.remove('theme-no-animations')
  }

  // 设置高对比度
  if (config.highContrast || prefersHighContrast()) {
    root.classList.add('theme-high-contrast')
  } else {
    root.classList.remove('theme-high-contrast')
  }

  // 更新meta标签颜色
  updateMetaThemeColor(config.primaryColor)

  console.log('Theme applied:', config)
}

/**
 * 更新meta主题颜色
 */
function updateMetaThemeColor(color: string): void {
  let metaThemeColor = document.querySelector('meta[name="theme-color"]')
  
  if (!metaThemeColor) {
    metaThemeColor = document.createElement('meta')
    metaThemeColor.setAttribute('name', 'theme-color')
    document.head.appendChild(metaThemeColor)
  }
  
  metaThemeColor.setAttribute('content', color)
}

/**
 * 切换主题模式
 */
export function toggleThemeMode(): ThemeMode {
  const currentMode = currentTheme.mode === 'auto' ? getSystemThemePreference() : currentTheme.mode
  const newMode: ThemeMode = currentMode === 'light' ? 'dark' : 'light'
  
  updateTheme({ mode: newMode })
  return newMode
}

/**
 * 设置主题变体
 */
export function setThemeVariant(variant: ThemeVariant): void {
  const variantConfig = THEME_VARIANTS[variant]
  updateTheme({
    variant,
    primaryColor: variantConfig.colors.primary,
    accentColor: variantConfig.colors.accent
  })
}

/**
 * 更新主题配置
 */
export function updateTheme(updates: Partial<ThemeConfig>): void {
  const newConfig = { ...currentTheme, ...updates }
  saveThemeConfig(newConfig)
  applyTheme(newConfig)
}

/**
 * 重置主题到默认配置
 */
export function resetTheme(): void {
  const defaultConfig = { ...DEFAULT_THEME_CONFIG }
  saveThemeConfig(defaultConfig)
  applyTheme(defaultConfig)
}

/**
 * 监听系统主题变化
 */
export function watchSystemTheme(callback?: (isDark: boolean) => void): () => void {
  if (typeof window === 'undefined') return () => {}

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  
  const handleChange = (e: MediaQueryListEvent) => {
    if (currentTheme.mode === 'auto') {
      applyTheme(currentTheme)
    }
    callback?.(e.matches)
  }

  mediaQuery.addEventListener('change', handleChange)
  
  return () => {
    mediaQuery.removeEventListener('change', handleChange)
  }
}

/**
 * 监听系统动画偏好变化
 */
export function watchReducedMotion(callback?: (prefersReduced: boolean) => void): () => void {
  if (typeof window === 'undefined') return () => {}

  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  
  const handleChange = (e: MediaQueryListEvent) => {
    if (e.matches) {
      updateTheme({ animations: false })
    }
    callback?.(e.matches)
  }

  mediaQuery.addEventListener('change', handleChange)
  
  return () => {
    mediaQuery.removeEventListener('change', handleChange)
  }
}

/**
 * 监听系统对比度偏好变化
 */
export function watchHighContrast(callback?: (prefersHigh: boolean) => void): () => void {
  if (typeof window === 'undefined') return () => {}

  const mediaQuery = window.matchMedia('(prefers-contrast: high)')
  
  const handleChange = (e: MediaQueryListEvent) => {
    if (e.matches) {
      updateTheme({ highContrast: true })
    }
    callback?.(e.matches)
  }

  mediaQuery.addEventListener('change', handleChange)
  
  return () => {
    mediaQuery.removeEventListener('change', handleChange)
  }
}

/**
 * 初始化主题系统
 */
export function initializeTheme(): () => void {
  // 加载并应用主题
  const config = loadThemeConfig()
  applyTheme(config)

  // 监听系统偏好变化
  const unwatchTheme = watchSystemTheme()
  const unwatchMotion = watchReducedMotion()
  const unwatchContrast = watchHighContrast()

  // 返回清理函数
  return () => {
    unwatchTheme()
    unwatchMotion()
    unwatchContrast()
  }
}

/**
 * 获取主题变体选项
 */
export function getThemeVariantOptions() {
  return Object.entries(THEME_VARIANTS).map(([key, variant]) => ({
    value: key as ThemeVariant,
    label: variant.name,
    description: variant.description,
    colors: variant.colors
  }))
}

/**
 * 生成主题CSS变量
 */
export function generateThemeCSS(config: ThemeConfig): string {
  const variant = THEME_VARIANTS[config.variant]
  
  return `
    :root {
      --theme-primary: ${config.primaryColor};
      --theme-accent: ${config.accentColor};
      --theme-success: ${variant.colors.success};
      --theme-warning: ${variant.colors.warning};
      --theme-error: ${variant.colors.error};
      --theme-info: ${variant.colors.info};
      --theme-font-size: ${config.fontSize}px;
      --theme-border-radius: ${config.borderRadius}px;
    }
  `
}

/**
 * 验证主题配置
 */
export function validateThemeConfig(config: Partial<ThemeConfig>): boolean {
  try {
    if (config.mode && !['light', 'dark', 'auto'].includes(config.mode)) {
      return false
    }
    
    if (config.variant && !Object.keys(THEME_VARIANTS).includes(config.variant)) {
      return false
    }
    
    if (config.primaryColor && !/^#[0-9A-F]{6}$/i.test(config.primaryColor)) {
      return false
    }
    
    if (config.accentColor && !/^#[0-9A-F]{6}$/i.test(config.accentColor)) {
      return false
    }
    
    if (config.fontSize && (config.fontSize < 12 || config.fontSize > 24)) {
      return false
    }
    
    if (config.borderRadius && (config.borderRadius < 0 || config.borderRadius > 20)) {
      return false
    }
    
    return true
  } catch (error) {
    console.warn('Theme config validation error:', error)
    return false
  }
}

/**
 * 导出主题配置
 */
export function exportThemeConfig(): string {
  return JSON.stringify(currentTheme, null, 2)
}

/**
 * 导入主题配置
 */
export function importThemeConfig(configString: string): boolean {
  try {
    const config = JSON.parse(configString)
    
    if (validateThemeConfig(config)) {
      const mergedConfig = { ...DEFAULT_THEME_CONFIG, ...config }
      saveThemeConfig(mergedConfig)
      applyTheme(mergedConfig)
      return true
    }
    
    return false
  } catch (error) {
    console.warn('Failed to import theme config:', error)
    return false
  }
}